import * as vp from 'vopay';
import * as Yup from 'yup';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { HubSpotDropdownDataContext, useHubSpotDropdownDataSet } from '../../providers/HubSpotDropdownData.provider';

import {
  buildMatcher,
  createEmailField,
  createLinkField,
  createNumberField,
  createSelectField,
  createTextAreaField,
  createTextField,
  createTextGoogleAddressFeild,
  createToggleField,
  createToolTip,
  matchers,
  useValidationMessages,
} from './useFields.helpers';

import fieldUtilsFactory from './fieldUtils.factory';
import { useBillingPackages } from '../../providers/BillingPackages.provider';

import type { NewAccountData } from '../../components/Accounts/types';
import type { FieldUtils, SubSection } from './types';
import type { MultiStepFormCtx } from '../../providers/MultiStepForm.provider';
import { useModuleFeatureFlag } from '../../providers/FeatureFlag.provider';
import { usePartnerLogin } from '../../providers/PartnerLogin.provider';
import { usePermissions } from '../../providers/Permissions.provider';
import { sections as BillingSections, BillingPackageCreationSections } from './useBillingPackageCreationFields';
import useFetchBusinessTypes from '../useApi/useFetchBusinessTypes';
import useFetchHubSpotDealDetails from '../useApi/useFetchHubSpotDealDetails';
import useDataFetchIndicator from '../useDataFetchIndicator';
import { useCompanyProfileFields } from '../../providers/CompanyProfileFields.provider';
import { useAccountTransactionTypes } from '../../providers/AccountTransactionTypes.provider';
import useAccountMatrixCreationFields from './useAccountMatrixCreationFields';
import { useBusinessTypeOptions, useCountryNames, useFetchBillingPackagesDetails } from '@/hooks';
import { useTranslation } from 'react-i18next';

export type AccountCreationSections = BillingPackageCreationSections & {
  companyProfile: SubSection<NewAccountData>[];
  companyFlowOfFunds: SubSection<NewAccountData>[];
  companyAddress: SubSection<NewAccountData>[];
  accountSettings: SubSection<NewAccountData>[];
  billingPackage: SubSection<NewAccountData>[];
};

export type AccountCreationSection = keyof AccountCreationSections;
export type Ctx = MultiStepFormCtx<NewAccountData>;

const useAccountCreationFields = (): FieldUtils<NewAccountData> => {
  const validationMessages = useValidationMessages();
  const { response } = usePartnerLogin();
  const hubSpotContext = useContext(HubSpotDropdownDataContext);
  const { isInsideSales, partnerType } = response;
  const { billingPackages, refreshBillingPackages } = useBillingPackages();
  const { setDealCountry } = useHubSpotDropdownDataSet();
  const permissions = useModuleFeatureFlag('accounts');
  const userPermissions = usePermissions();
  const { makeFieldsVisible, setMakeFieldsVisible, fromReviewPage } = useCompanyProfileFields();
  const { fetchPartnerTransactionTypes } = useAccountTransactionTypes();
  const companyName = useRef<string>('');
  const [, getDealDetails] = useFetchHubSpotDealDetails({});
  const billingPermissions = useModuleFeatureFlag('billingPackages');
  const [{ packageDetails }, getPackageDetails] = useFetchBillingPackagesDetails({});
  const { t } = useTranslation('translations', { keyPrefix: 'Hooks.UseFields.UseBillingPackageCreationFields' });
  const { t: tAccount } = useTranslation('translations', { keyPrefix: 'Hooks.UseFields.UseAccountCreationFields' });
  const { countryOptions } = useCountryNames();
  // Localized display labels for BUSINESS_TYPES_OPTIONS; the option *keys* stay the submitted values.
  const businessTypeOptions = useBusinessTypeOptions();

  const billingPackageSections: BillingPackageCreationSections = BillingSections(
    billingPermissions,
    !isInsideSales,
    true,
    t,
    validationMessages,
    packageDetails?.PlanSettings,
  );

  const accountMatrixFields: FieldUtils<NewAccountData> = useAccountMatrixCreationFields();
  const [businessTypes, fetchBusinessTypes] = useFetchBusinessTypes({});
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const [hubSpotDealId, setHubSpotDealId] = useState<string>('');

  type FieldMapType = Map<string, boolean>;

  const [showCompanyProfileFieldMap] = useState<FieldMapType>(
    new Map<string, boolean>([
      ['hubspotDealId', isInsideSales],
      ['legalBusinessName', true],
      ['billingCountry', true],
      ['businessName', true],
      ['businessModel', permissions.canSeeBusinessCase],
      ['businessType', true],
      ['industry', true],
      ['industrySubCategory', true],
      ['numberOfYears', permissions.canSeeBusinessCase],
      ['flowOfFunds', permissions.canSeeBusinessCase],
      ['emailAddress', true],
      ['websiteURL', true],
      ['hubspotCompanyId', isInsideSales],
      ['platformUsage', permissions.canSeeBusinessCase],
      ['isMSB', permissions.canSeeBusinessCase],
      ['notes', permissions.canSeeBusinessCase],
      ['dataAggregator', isInsideSales],
      ['systemOfRecord', isInsideSales],
      ['createBillingPackage', false],
    ]),
  );

  const [showCompanyProfileFieldForInsideSalesMap] = useState<FieldMapType>(
    new Map<string, boolean>([
      ['hubspotDealId', true],
      ['hubspotCompanyId', true],
      ['hubspotCountry', true],
      ['accountLink', true],
      ['firstName', true],
      ['lastName', true],
      ['companyBusinessName', true],
      ['companyEmailAddress', true],
      ['companyWebsite', true],
      ['flowOfFundsDiagram', true],
      ['legalAgreement', true],
      ['companyFlowOfFunds', true],
      ['notes', true],
      ['companyPlatformUsage', true],
      ['accountingSystem', true],
      ['socialMediaPage', true],
      ['dataAggregator', true],
      ['companyPhone', true],
    ]),
  );

  const [industryData, setIndustryData] = useState<{ [key: string]: string }>({});
  const [platformnUsagesData, setPlatformnUsagesData] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    fetchBusinessTypes();
  }, []);

  useEffect(() => {
    setIndustryData(hubSpotContext.hubSpotDropDownData.industriesDropDownOptions);
    setPlatformnUsagesData(hubSpotContext.hubSpotDropDownData.platformUsagesDropDownOptions);
  }, []);

  useEffect(() => {
    if (hubSpotDealId === '' && !fromReviewPage) {
      setMakeFieldsVisible(false);
    }
  }, [hubSpotDealId]);

  const populateHubSpot = (accountHubSpotDealID: string, ctx: Ctx) => {
    setHubSpotDealId(accountHubSpotDealID);

    const fetchData = async () => {
      try {
        if (accountHubSpotDealID || makeFieldsVisible) {
          setDataFetchingInfo(true, tAccount('loadingData', { defaultValue: 'Loading Data' }), 'fullScreen');
          const hubSpotDetails = await getDealDetails(accountHubSpotDealID);

          if (hubSpotDetails.Success) {
            if (hubSpotDetails.Details.CompanyHubspotID) {
              setMakeFieldsVisible(isInsideSales);
              companyName.current = hubSpotDetails.Details.CompanyName;

              ctx.handleFieldChange(
                vp.createSyntheticEvent('hubspotCompanyId', hubSpotDetails.Details.CompanyHubspotID),
              );

              ctx.handleFieldChange(vp.createSyntheticEvent('hubspotCountry', hubSpotDetails.Details.Country));
              ctx.handleFieldChange(vp.createSyntheticEvent('billingCountry', hubSpotDetails.Details.DealCountry));
              ctx.handleFieldChange(vp.createSyntheticEvent('accountLink', hubSpotDetails.Details.AccountLink));

              ctx.handleFieldChange(
                vp.createSyntheticEvent('companyPlatformUsage', hubSpotDetails.Details.PlatformUsage),
              );

              ctx.handleFieldChange(vp.createSyntheticEvent('socialMediaPage', hubSpotDetails.Details.SocialMediaPage));
              ctx.handleFieldChange(vp.createSyntheticEvent('companyPhone', hubSpotDetails.Details.CompanyPhone));

              ctx.handleFieldChange(
                vp.createSyntheticEvent('companyWebsiteURL', hubSpotDetails.Details.CompanyWebsite),
              );

              refreshBillingPackages({ useCached: false, country: hubSpotDetails.Details.DealCountry ?? '' });
              setDealCountry(hubSpotDetails.Details.DealCountry ?? '');
              fetchPartnerTransactionTypes({});
            } else {
              setMakeFieldsVisible(false);
              ctx.handleFieldChange(vp.createSyntheticEvent('hubspotDealId', ''));
            }
          } else {
            setMakeFieldsVisible(false);
            ctx.handleFieldChange(vp.createSyntheticEvent('hubspotDealId', ''));
            ctx.handleFieldChange(vp.createSyntheticEvent('hubspotCompanyId', ''));
            ctx.handleFieldChange(vp.createSyntheticEvent('hubspotCountry', ''));
            ctx.handleFieldChange(vp.createSyntheticEvent('accountLink', ''));
            ctx.handleFieldChange(vp.createSyntheticEvent('companyPlatformUsage', ''));
            ctx.handleFieldChange(vp.createSyntheticEvent('socialMediaPage', ''));
            ctx.handleFieldChange(vp.createSyntheticEvent('companyPhone', ''));
            ctx.handleFieldChange(vp.createSyntheticEvent('companyWebsiteURL', ''));
          }

          setDataFetchingInfo(false);
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchData().catch((error) => {
      console.error(error);
    });
  };

  const showCompanyProfileField = (key: string, isReview = false): boolean => {
    if (isInsideSales) {
      if (key === 'hubspotDealId' || isReview) {
        return true;
      }

      return makeFieldsVisible && (showCompanyProfileFieldForInsideSalesMap.get(key) ?? false);
    }

    return showCompanyProfileFieldMap.get(key) ?? false;
  };

  const getAccountLinkName = () =>
    tAccount('hubSpotLink', { defaultValue: 'HubSpot Link {{companyName}}', companyName: companyName.current });

  const sections: AccountCreationSections = {
    companyProfile: [
      {
        label: tAccount('companyProfileTitle', { defaultValue: 'Company Profile' }),
        fields: {
          hubspotDealId: {
            label: tAccount('hubspotDealID', { defaultValue: 'Hubspot Deal ID' }),
            placeholder: tAccount('hubspotDealID', { defaultValue: 'Hubspot Deal ID' }),
            fieldName: 'hubspotDealId',
            initValue: '',
            dataType: 'number',
            getInputState: () => (isInsideSales ? 'required' : 'disabled'),
            showField: (_ctx: Ctx, isReview = false) => showCompanyProfileField('hubspotDealId', isReview),
            getElement(ctx: Ctx) {
              const textElement = createTextField(this, ctx, undefined, undefined, (event) => {
                if (event && event.target) {
                  populateHubSpot(event.target.value, ctx);
                }
              });

              return textElement;
            },
            validate() {
              if (isInsideSales) {
                return Yup.number().required(
                  tAccount('hubSpotIdInvalid', {
                    defaultValue: 'HubSpot ID is incorrect. Please enter a valid HubSpot ID.',
                  }),
                );
              }

              return Yup.number().notRequired();
            },
          },
          hubspotCompanyId: {
            label: tAccount('hubspotCompanyID', { defaultValue: 'Hubspot Company ID' }),
            placeholder: tAccount('hubspotCompanyID', { defaultValue: 'Hubspot Company ID' }),
            fieldName: 'hubspotCompanyId',
            initValue: '',
            dataType: 'number',
            getInputState: () => (isInsideSales ? 'disabled' : 'disabled'),
            showField: (_ctx: Ctx, isReview = false) => showCompanyProfileField('hubspotCompanyId', isReview),
            getElement(ctx: Ctx) {
              return createTextField(this, ctx);
            },
            validate() {
              if (isInsideSales) {
                return Yup.number().required(
                  tAccount('hubspotCompanyIdRequired', { defaultValue: 'Please enter a Hubspot Company ID.' }),
                );
              }

              return Yup.number().notRequired();
            },
          },
          hubspotCountry: {
            label: tAccount('country', { defaultValue: 'Country' }),
            placeholder: tAccount('hubSpotCountryPlaceholder', { defaultValue: 'HubSpot Country' }),
            fieldName: 'hubspotCountry',
            initValue: '',
            dataType: 'text',
            getInputState: () => (isInsideSales ? 'disabled' : 'disabled'),
            showField: (_ctx: Ctx, isReview = false) => showCompanyProfileField('hubspotCountry', isReview),
            getElement(ctx: Ctx) {
              return createTextField(this, ctx);
            },
            validate() {
              if (isInsideSales) {
                return Yup.string().required(
                  tAccount('countryRequired', { defaultValue: 'Please enter the Country.' }),
                );
              }

              return Yup.string().notRequired();
            },
          },
          legalBusinessName: {
            label: tAccount('legalBusinessName', { defaultValue: 'Legal Business Name ' }),
            placeholder: tAccount('legalBusinessName', { defaultValue: 'Legal Business Name ' }),
            fieldName: 'legalBusinessName',
            initValue: '',
            dataType: 'text',
            showField: (_ctx: Ctx, isReview = false) => showCompanyProfileField('legalBusinessName', isReview),
            getInputState: () => 'required',
            getElement(ctx: Ctx) {
              return createTextField(this, ctx);
            },
            validate() {
              if (!isInsideSales) {
                return Yup.string()
                  .matches(
                    new RegExp(
                      buildMatcher([matchers.alpha, matchers.numeric, matchers.whitespace, matchers.hyphen]),
                      'i',
                    ),
                    validationMessages.noSpecialChars,
                  )
                  .required(
                    tAccount('legalBusinessNameRequired', { defaultValue: 'Please enter a legal business name.' }),
                  );
              }

              return Yup.string().notRequired();
            },
          },
          businessName: {
            label: tAccount('businessName', { defaultValue: 'Business Name' }),
            placeholder: tAccount('businessName', { defaultValue: 'Business Name' }),
            fieldName: 'businessName',
            initValue: '',
            dataType: 'text',
            showField: (_ctx: Ctx, isReview = false) => showCompanyProfileField('businessName', isReview),
            getInputState: () => 'required',
            getElement(ctx: Ctx) {
              return createTextField(this, ctx);
            },
            validate() {
              if (!isInsideSales) {
                return Yup.string()
                  .matches(
                    new RegExp(
                      buildMatcher([matchers.alpha, matchers.numeric, matchers.whitespace, matchers.hyphen]),
                      'i',
                    ),
                    validationMessages.noSpecialChars,
                  )
                  .required(tAccount('businessNameRequired', { defaultValue: 'Please enter a business name.' }));
              }

              return Yup.string().notRequired();
            },
          },
          billingCountry: {
            label: tAccount('country', { defaultValue: 'Country' }),
            placeholder: tAccount('selectACountry', { defaultValue: 'Select a Country' }),
            fieldName: 'billingCountry',
            initValue: '',
            dataType: 'mapped',
            getInputState: () => (isInsideSales ? 'optional' : 'required'),
            showField: (_ctx: Ctx, isReview = false) => showCompanyProfileField('billingCountry', isReview),
            getElement(ctx: Ctx) {
              return createSelectField(this, ctx, {}, (event) => {
                if (event && event.target) {
                  refreshBillingPackages({ useCached: false, country: event.target.value ?? '' });
                }
              });
            },
            getSelectOptions: () => countryOptions,
            validate() {
              if (!isInsideSales) {
                return Yup.string().required(
                  tAccount('selectCountryRequired', { defaultValue: 'Please select a country.' }),
                );
              }

              return Yup.string().notRequired();
            },
          },
          industry: {
            label: tAccount('industry', { defaultValue: 'Industry' }),
            placeholder: tAccount('industry', { defaultValue: 'Industry' }),
            fieldName: 'industry',
            initValue: '',
            dataType: 'mapped',
            showField: (_ctx: Ctx, isReview = false) => showCompanyProfileField('industry', isReview),
            getInputState: () => (isInsideSales ? 'required' : 'optional'),
            getSelectOptions: () => {
              if (isInsideSales) {
                return industryData;
              }

              const optionsIndustry: { [key: string]: string } = {};

              Object.values(businessTypes.BusinessTypes).forEach((type) => {
                optionsIndustry[type.BusinessTypeID] = type.Industry;
              });

              return optionsIndustry;
            },
            getElement(ctx: Ctx) {
              return createSelectField(this, ctx, { noOrdering: true });
            },
            validate() {
              if (!isInsideSales) {
                return Yup.string().required(tAccount('industryRequired', { defaultValue: 'Please select Industry.' }));
              }

              return Yup.string().notRequired();
            },
          },
          industrySubCategory: {
            label: tAccount('industrySubCategory', { defaultValue: 'Industry Sub-Category' }),
            placeholder: tAccount('selectASubCategory', { defaultValue: 'Select a Sub-Category' }),
            fieldName: 'industrySubCategory',
            initValue: '',
            dataType: 'mapped',
            getInputState: () => 'required',
            showField: (_ctx: Ctx, isReview = false) => showCompanyProfileField('industrySubCategory', isReview),
            getElement(ctx: Ctx) {
              return createSelectField(this, ctx, { noOrdering: true });
            },
            getSelectOptions: (ctx: Ctx) => {
              const optionsSubInsdustry: { [key: string]: string } = {};

              if (ctx.values.industry) {
                Object.values(
                  Object.values(businessTypes.BusinessTypes || {}).find(
                    (industry) => industry.BusinessTypeID === ctx.values.industry,
                  )?.Categories || {},
                ).forEach((category) => {
                  optionsSubInsdustry[category.BusinessTypeCategoryID] = category.Category;
                });
              }

              return optionsSubInsdustry;
            },
            resetOnUpdate: ['industry'],
          },
          accountLink: {
            label: tAccount('linkOfTheAccount', { defaultValue: 'Link of the Account' }),
            placeholder: tAccount('linkOfTheAccount', { defaultValue: 'Link of the Account' }),
            fieldName: 'accountLink',
            initValue: '',
            dataType: 'link',
            target: '_blank',
            linkName: () => getAccountLinkName(),
            getInputState: () => (isInsideSales ? 'disabled' : 'disabled'),
            showField: (_ctx: Ctx, isReview = false) => showCompanyProfileField('accountLink', isReview),
            getElement(ctx: Ctx) {
              return createLinkField(this, ctx, getAccountLinkName());
            },
            validate() {
              if (isInsideSales) {
                return Yup.string().required(
                  tAccount('accountLinkRequired', { defaultValue: 'Please enter the link for the account.' }),
                );
              }

              return Yup.string().notRequired();
            },
          },
          businessModel: {
            label: tAccount('businessModel', { defaultValue: 'Business Model' }),
            placeholder: tAccount('businessModel', { defaultValue: 'Business Model' }),
            fieldName: 'businessModel',
            initValue: '',
            dataType: 'text', // text area change
            showField: (_ctx: Ctx, isReview = false) => showCompanyProfileField('businessModel', isReview),
            getInputState: () => (permissions.canSeeBusinessCase ? 'required' : 'optional'),
            getElement(ctx: Ctx) {
              return createTextField(this, ctx);
            },
            validate(ctx: Ctx) {
              if (!isInsideSales) {
                return this.getInputState(ctx) === 'required'
                  ? Yup.string()
                      .matches(
                        /^[^*|<>[\]{}`]+$/,
                        tAccount('invalidCharacters', {
                          defaultValue: 'The characters { } [ ] < > | * and ` are not allowed.',
                        }),
                      )
                      .required(
                        tAccount('businessModelRequired', { defaultValue: 'Please describe the business model.' }),
                      )
                  : Yup.string().notRequired();
              }

              return Yup.string().notRequired();
            },
          },
          businessType: {
            label: tAccount('businessType', { defaultValue: 'Business Type' }),
            placeholder: tAccount('selectABusinessType', { defaultValue: 'Select a Business Type' }),
            fieldName: 'businessType',
            initValue: '',
            dataType: 'mapped',
            getInputState: () => 'required',
            showField: (_ctx: Ctx, isReview = false) => showCompanyProfileField('businessType', isReview),
            getElement(ctx: Ctx) {
              return createSelectField(this, ctx, { noOrdering: true });
            },
            getSelectOptions: () => businessTypeOptions,
          },
          numberOfYears: {
            label: tAccount('numberOfYearsInBusiness', { defaultValue: 'Number of Years in Business' }),
            placeholder: '1',
            fieldName: 'numberOfYears',
            initValue: '',
            dataType: 'number',
            showField: (_ctx: Ctx, isReview = false) => showCompanyProfileField('numberOfYears', isReview),
            getInputState: () => (permissions.canSeeBusinessCase ? 'required' : 'optional'),
            getElement(ctx: Ctx) {
              return createNumberField(this, ctx);
            },
            validate(ctx: Ctx) {
              if (!isInsideSales) {
                const base = Yup.number()
                  .transform((_, v: string) => parseFloat(v.replace(/,/g, '')))
                  .min(1, validationMessages.minOne);

                return this.getInputState(ctx) === 'required'
                  ? base.required(
                      tAccount('numberOfYearsRequired', {
                        defaultValue: 'Please enter the number of years you have been in business.',
                      }),
                    )
                  : base.notRequired();
              }

              return Yup.number().notRequired();
            },
          },
          flowOfFunds: {
            label: tAccount('flowOfFunds', { defaultValue: 'Flow of Funds' }),
            placeholder: tAccount('flowOfFunds', { defaultValue: 'Flow of Funds' }),
            fieldName: 'flowOfFunds',
            initValue: '',
            dataType: 'text',
            showField: () => !isInsideSales,
            getInputState: () => (permissions.canSeeBusinessCase ? 'required' : 'optional'),
            getElement(ctx: Ctx) {
              return createTextField(this, ctx);
            },
            validate(ctx: Ctx) {
              if (!isInsideSales) {
                return this.getInputState(ctx) === 'required'
                  ? Yup.string()
                      .matches(
                        new RegExp(buildMatcher([matchers.alpha, matchers.numeric, matchers.whitespace]), 'i'),
                        validationMessages.noSpecialChars,
                      )
                      .required(
                        tAccount('flowOfFundsRequired', { defaultValue: 'Please describe your flow of funds.' }),
                      )
                  : Yup.string().notRequired();
              }

              return Yup.string().notRequired();
            },
          },
          businessEmailAddress: {
            label: tAccount('businessEmail', { defaultValue: 'Business Email ' }),
            placeholder: 'example@email.com',
            fieldName: 'businessEmailAddress',
            initValue: '',
            dataType: 'email',
            getInputState: () => 'required',
            showField: () => !isInsideSales,
            getElement(ctx: Ctx) {
              return createEmailField(this, ctx);
            },
            validate() {
              if (!isInsideSales) {
                return Yup.string().email(validationMessages.invalidEmail).required(validationMessages.missingEmail);
              }

              return Yup.string().notRequired();
            },
          },
          websiteURL: {
            label: tAccount('website', { defaultValue: 'Website' }),
            placeholder: tAccount('website', { defaultValue: 'Website' }),
            fieldName: 'websiteURL',
            initValue: '',
            dataType: 'text',
            showField: () => !isInsideSales,
            getInputState: () => 'required',
            getElement(ctx: Ctx) {
              return createTextField(this, ctx);
            },
            validate() {
              if (!isInsideSales) {
                return Yup.string()
                  .matches(
                    /^[^*|<>[\]{}`]+$/,
                    tAccount('invalidCharacters', {
                      defaultValue: 'The characters { } [ ] < > | * and ` are not allowed.',
                    }),
                  )
                  .required(tAccount('websiteURLRequired', { defaultValue: 'Please enter the business website URL.' }));
              }

              return Yup.string().notRequired();
            },
          },
          platformUsage: {
            label: tAccount('platformUsage', { defaultValue: 'Platform Usage' }),
            placeholder: tAccount('platformUsage', { defaultValue: 'Platform Usage' }),
            fieldName: 'platformUsage',
            initValue: '',
            dataType: 'mapped',
            showField: () => !isInsideSales,
            getInputState: () => (permissions.canSeeBusinessCase ? 'required' : 'optional'),
            getSelectOptions: () => platformnUsagesData,
            getElement(ctx: Ctx) {
              return createSelectField(this, ctx, { noOrdering: true });
            },
            validate() {
              if (!isInsideSales) {
                return permissions.canSeeBusinessCase
                  ? Yup.string().required(
                      tAccount('platformUsageRequired', { defaultValue: 'Please select platform usage.' }),
                    )
                  : Yup.string().notRequired();
              }

              return Yup.string().notRequired();
            },
          },
          isMSB: {
            label: tAccount('isMSB', { defaultValue: 'Is this a money service business (MSB)?' }),
            fieldName: 'isMSB',
            initValue: false,
            dataType: 'boolean',
            showField: (_ctx: Ctx, isReview = false) => showCompanyProfileField('isMSB', isReview),
            getInputState: () => (permissions.canSeeBusinessCase ? 'required' : 'optional'),
            getElement(ctx: Ctx) {
              return createToggleField(this, ctx);
            },
          },
          createBillingPackage: {
            label: tAccount('createBillingPackage', { defaultValue: 'Create Billing Package?' }),
            fieldName: 'createBillingPackage',
            initValue: false,
            dataType: 'boolean',
            showField: (_ctx: Ctx, isReview = false) =>
              isReview && isInsideSales ? false : showCompanyProfileField('createBillingPackage', isReview),
            getInputState: () => 'optional',
            getElement(ctx: Ctx) {
              return createToggleField(this, ctx);
            },
          },
          notes: {
            label: tAccount('notes', { defaultValue: 'Notes' }),
            placeholder: tAccount('notes', { defaultValue: 'Notes' }),
            fieldName: 'notes',
            initValue: '',
            dataType: 'text',
            showField: () => !isInsideSales,
            getInputState: () => 'optional',
            getElement(ctx: Ctx) {
              return createTextField(this, ctx);
            },
          },
          dataAggregator: {
            label: tAccount('dataAggregator', { defaultValue: 'Data Aggregator' }),
            placeholder: tAccount('pleaseSelectOne', { defaultValue: 'Please select one' }),
            fieldName: 'dataAggregator',
            initValue: '',
            dataType: 'mapped',
            showField: () => !isInsideSales,
            getInputState: () => 'optional',
            getElement(ctx: Ctx) {
              return createSelectField(this, ctx, { noOrdering: true });
            },
            getSelectOptions: () => vp.DATA_AGGREGATOR_OPTIONS,
          },
          systemOfRecord: {
            label: tAccount('systemOfRecord', { defaultValue: 'System Of Record' }),
            placeholder: tAccount('systemOfRecordPlaceholder', { defaultValue: 'eg: Sage, Quickbooks, TurnKey, etc.' }),
            fieldName: 'systemOfRecord',
            initValue: '',
            dataType: 'text',
            showField: () => !isInsideSales,
            getInputState: () => 'optional',
            getElement(ctx: Ctx) {
              return createTextField(this, ctx);
            },
          },
        },
        showSection: () => true,
      },
      {
        label: tAccount('primaryContactInformationTitle', { defaultValue: 'Primary Contact Information' }),
        fields: {
          firstName: {
            label: tAccount('firstName', { defaultValue: 'First Name' }),
            placeholder: tAccount('firstName', { defaultValue: 'First Name' }),
            fieldName: 'firstName',
            initValue: '',
            dataType: 'text',
            showField: (_ctx: Ctx, isReview = false) => showCompanyProfileField('firstName', isReview),
            getInputState: () => (isInsideSales ? 'required' : 'disabled'),
            getElement(ctx: Ctx) {
              return createTextField(this, ctx);
            },
            validate() {
              if (isInsideSales) {
                return Yup.string()
                  .matches(
                    new RegExp(buildMatcher([matchers.alpha, matchers.numeric, matchers.whitespace]), 'i'),
                    validationMessages.noSpecialChars,
                  )
                  .required(tAccount('firstNameRequired', { defaultValue: 'Please enter the first name.' }));
              }

              return Yup.string().notRequired();
            },
          },
          lastName: {
            label: tAccount('lastName', { defaultValue: 'Last Name' }),
            placeholder: tAccount('lastName', { defaultValue: 'Last Name' }),
            fieldName: 'lastName',
            initValue: '',
            dataType: 'text',
            showField: (_ctx: Ctx, isReview = false) => showCompanyProfileField('lastName', isReview),
            getInputState: () => (isInsideSales ? 'required' : 'disabled'),
            getElement(ctx: Ctx) {
              return createTextField(this, ctx);
            },
            validate() {
              if (isInsideSales) {
                return Yup.string()
                  .matches(
                    new RegExp(buildMatcher([matchers.alpha, matchers.numeric, matchers.whitespace]), 'i'),
                    validationMessages.noSpecialChars,
                  )
                  .required(tAccount('lastNameRequired', { defaultValue: 'Please enter the last name.' }));
              }

              return Yup.string().notRequired();
            },
          },
          companyBusinessName: {
            label: tAccount('businessName', { defaultValue: 'Business Name' }),
            placeholder: tAccount('businessName', { defaultValue: 'Business Name' }),
            fieldName: 'companyBusinessName',
            initValue: '',
            dataType: 'text',
            showField: (_ctx: Ctx, isReview = false) => showCompanyProfileField('companyBusinessName', isReview),
            getInputState: () => (isInsideSales ? 'required' : 'optional'),
            getElement(ctx: Ctx) {
              return createTextField(this, ctx);
            },
            validate(ctx: Ctx) {
              return this.getInputState(ctx) === 'required'
                ? Yup.string()
                    .matches(
                      new RegExp(
                        buildMatcher([
                          matchers.alpha,
                          matchers.numeric,
                          matchers.whitespace,
                          matchers.hyphen,
                          matchers.alpha,
                          matchers.numeric,
                        ]),
                        'i',
                      ),
                      validationMessages.noSpecialChars,
                    )
                    .required(
                      tAccount('primaryContactNameRequired', {
                        defaultValue: 'Please enter a primary contact full name.',
                      }),
                    )
                : Yup.string().notRequired();
            },
          },
          emailAddress: {
            label: tAccount('emailAddress', { defaultValue: 'Email Address' }),
            placeholder: 'name@domain.com',
            fieldName: 'emailAddress',
            initValue: '',
            dataType: 'email',
            getInputState: () => (isInsideSales ? 'required' : 'optional'),
            showField: (_ctx: Ctx, isReview = false) => showCompanyProfileField('companyEmailAddress', isReview),
            getElement(ctx: Ctx) {
              return createEmailField(this, ctx);
            },
            validate() {
              if (isInsideSales) {
                return Yup.string().email(validationMessages.invalidEmail).required(validationMessages.missingEmail);
              }

              return Yup.string().notRequired();
            },
          },
        },
        showSection: () => isInsideSales,
      },
      {
        label: tAccount('accountSettings', { defaultValue: 'Account Settings' }),
        fields: {
          companyPlatformUsage: {
            label: tAccount('platformUsage', { defaultValue: 'Platform Usage' }),
            placeholder: tAccount('platformUsage', { defaultValue: 'Platform Usage' }),
            fieldName: 'companyPlatformUsage',
            initValue: '',
            dataType: 'mapped',
            showField: (_ctx: Ctx, isReview = false) => showCompanyProfileField('companyPlatformUsage', isReview),
            getInputState: () => (permissions.canSeeBusinessCase ? 'required' : 'optional'),
            getSelectOptions: () => platformnUsagesData,
            getElement(ctx: Ctx) {
              return createSelectField(this, ctx, { noOrdering: true });
            },
            validate(ctx: Ctx) {
              if (isInsideSales) {
                return this.getInputState(ctx) === 'required'
                  ? Yup.string().required(
                      tAccount('platformUsageRequired', { defaultValue: 'Please select platform usage.' }),
                    )
                  : Yup.string().notRequired();
              }

              return Yup.string().notRequired();
            },
          },
          accountingSystem: {
            label: tAccount('accountingSystem', { defaultValue: 'Accounting System' }),
            placeholder: tAccount('selectAnAccountingSystem', { defaultValue: 'Select a Accounting System' }),
            fieldName: 'accountingSystem',
            initValue: '',
            dataType: 'mapped',
            showField: (_ctx: Ctx, isReview = false) => showCompanyProfileField('accountingSystem', isReview),
            getInputState: () => 'optional',
            getSelectOptions: () => vp.ACCOUNTING_SYSTEM_OPTIONS,
            getElement(ctx: Ctx) {
              return createSelectField(this, ctx, { noOrdering: true });
            },
          },
          dataAggregator: {
            label: tAccount('dataAggregator', { defaultValue: 'Data Aggregator' }),
            placeholder: tAccount('selectADataAggregator', { defaultValue: 'Select a Data Aggregator' }),
            fieldName: 'dataAggregator',
            initValue: '',
            dataType: 'mapped',
            showField: (_ctx: Ctx, isReview = false) => showCompanyProfileField('dataAggregator', isReview),
            getInputState: () => 'optional',
            getElement(ctx: Ctx) {
              return createSelectField(this, ctx, { noOrdering: true });
            },
            getSelectOptions: () => vp.DATA_AGGREGATOR_OPTIONS,
          },
        },
        showSection: () => isInsideSales,
      },
      {
        label: tAccount('businessInformationTitle', { defaultValue: 'Business Information' }),
        fields: {
          companyWebsiteURL: {
            label: tAccount('companyWebsite', { defaultValue: 'Company Website' }),
            placeholder: tAccount('website', { defaultValue: 'Website' }),
            fieldName: 'companyWebsiteURL',
            initValue: '',
            dataType: 'text',
            showField: (_ctx: Ctx, isReview = false) => showCompanyProfileField('companyWebsite', isReview),
            getInputState: () => 'disabled',
            getElement(ctx: Ctx) {
              return createTextField(this, ctx);
            },
            validate() {
              if (isInsideSales) {
                return Yup.string()
                  .matches(
                    /^[^*|<>[\]{}`]+$/,
                    tAccount('invalidCharacters', {
                      defaultValue: 'The characters { } [ ] < > | * and ` are not allowed.',
                    }),
                  )
                  .required(
                    tAccount('companyWebsiteURLRequired', { defaultValue: 'Please enter the company website URL.' }),
                  );
              }

              return Yup.string().notRequired();
            },
          },
          socialMediaPage: {
            label: tAccount('businessSocialMediaLink', { defaultValue: 'Business Social Media Link' }),
            placeholder: tAccount('socialMediaLink', { defaultValue: 'Social Media Link' }),
            fieldName: 'socialMediaPage',
            initValue: '',
            dataType: 'text',
            showField: (_ctx: Ctx, isReview = false) => showCompanyProfileField('socialMediaPage', isReview),
            getInputState: () => 'disabled',
            getSelectOptions: () => platformnUsagesData,
            getElement(ctx: Ctx) {
              return createTextField(this, ctx);
            },
            validate() {
              if (isInsideSales) {
                return Yup.string()
                  .matches(
                    /^(https?):\/\/[^\s/$?#].[^\s]*$/,
                    tAccount('socialMediaLinkInvalid', { defaultValue: 'The link has invalid characters.' }),
                  )
                  .required(
                    tAccount('socialMediaLinkRequired', { defaultValue: 'Please enter the social media link.' }),
                  );
              }

              return Yup.string().notRequired();
            },
          },
          companyPhone: {
            label: tAccount('businessPhoneNumber', { defaultValue: 'Business Phone Number' }),
            placeholder: '6041111111',
            fieldName: 'companyPhone',
            initValue: '',
            dataType: 'text',
            getInputState: () => 'disabled',
            showField: (_ctx: Ctx, isReview = false) => showCompanyProfileField('companyPhone', isReview),
            getElement(ctx: Ctx) {
              return createTextField(this, ctx);
            },
            validate() {
              return (
                isInsideSales &&
                Yup.string()
                  .matches(new RegExp(buildMatcher([matchers.numeric]), 'i'), validationMessages.numbers)
                  .test(
                    'len',
                    tAccount('phoneNumberLength', {
                      defaultValue: 'Phone number must be between 6 and 11 characters',
                    }),
                    (val) => (val || '').length >= 6 && (val || '').length <= 11,
                  )
                  .required(
                    tAccount('businessPhoneRequired', { defaultValue: 'Please enter the business phone number.' }),
                  )
              );
            },
          },
          notes: {
            label: tAccount('additionalNotes', { defaultValue: 'Additional Notes' }),
            placeholder: tAccount('additionalNotesPlaceholder', { defaultValue: 'Enter additional notes...' }),
            fieldName: 'notes',
            initValue: '',
            dataType: 'textarea',
            showField: (_ctx: Ctx, isReview = false) => showCompanyProfileField('notes', isReview),
            getInputState: () => 'optional',
            toolTip: () =>
              createToolTip(tAccount('additionalNotesTooltip', { defaultValue: 'Add any relevant account details' })),
            getElement(ctx: Ctx) {
              return createTextAreaField(this, ctx);
            },
            formatValue: true,
            maxLength: 1000,
            showcaseLength: true,
            resizeValue: 'vertical',
          },
        },
        showSection: () => isInsideSales,
      },
      {
        label: tAccount('legalAgreementTitle', { defaultValue: 'Legal Agreement' }),
        fields: {
          legalAgreement: {
            label: tAccount('legalAgreementNotes', { defaultValue: 'Legal Agreement Notes' }),
            placeholder: tAccount('legalAgreementNotesPlaceholder', { defaultValue: 'Enter legal agreement notes...' }),
            fieldName: 'legalAgreementNotes',
            initValue: '',
            dataType: 'textarea',
            showField: (_ctx: Ctx, isReview = false) => showCompanyProfileField('legalAgreement', isReview),
            getInputState: () => 'optional',
            toolTip: () =>
              createToolTip(
                tAccount('legalAgreementNotesTooltip', { defaultValue: 'Notes will appear on the legal agreement' }),
              ),
            getElement(ctx: Ctx) {
              return createTextAreaField(this, ctx);
            },
            formatValue: true,
            maxLength: 1000,
            showcaseLength: true,
            resizeValue: 'vertical',
          },
        },
        showSection: () => isInsideSales,
      },
    ],
    companyFlowOfFunds: [...accountMatrixFields.getSection('flowOfFunds'), ...accountMatrixFields.getSection('matrix')],
    companyAddress: [
      {
        label: tAccount('companyAddressTitle', { defaultValue: 'Company Address' }),
        fields: {
          address: {
            label: tAccount('address', { defaultValue: 'Address' }),
            placeholder: tAccount('address', { defaultValue: 'Address' }),
            fieldName: 'address',
            initValue: '',
            dataType: 'text',
            getGoogleCountries: () =>
              userPermissions.permissions.CanAddCanadianAccounts && userPermissions.permissions.CanAddUSAccounts
                ? vp.CANADA_US_COUNTRY_OPTION
                : userPermissions.permissions.CanAddUSAccounts
                  ? vp.US_COUNTRY_OPTION
                  : userPermissions.permissions.CanAddCanadianAccounts
                    ? vp.CANADA_COUNTRY_OPTION
                    : {},
            getInputState: () => 'required',
            showField: () => !isInsideSales,
            handleGoogleResults: (ctx: Ctx, addressComponents: vp.AddressComponents) => {
              if (!isInsideSales) {
                ctx.handleFieldChange(vp.createSyntheticEvent('address', addressComponents.address));
                ctx.handleFieldChange(vp.createSyntheticEvent('city', addressComponents.city));
                ctx.handleFieldChange(vp.createSyntheticEvent('province', addressComponents.province));
                ctx.handleFieldChange(vp.createSyntheticEvent('country', addressComponents.country));
                ctx.handleFieldChange(vp.createSyntheticEvent('postalcode', addressComponents.postalcode));
              }
            },
            getElement(ctx: Ctx) {
              return createTextGoogleAddressFeild(this, ctx);
            },

            validate() {
              return (
                !isInsideSales &&
                Yup.string()
                  .required(tAccount('addressRequired', { defaultValue: 'Please enter a address.' }))
                  .test(
                    'valid-address-values',
                    validationMessages.noSpecialChars,
                    (value) => !/[^\w ,:'()/\-.]|\s{5}/.test(value || ''),
                  )
              );
            },
          },
          unit: {
            label: tAccount('unit', { defaultValue: 'Unit' }),
            placeholder: tAccount('unit', { defaultValue: 'Unit' }),
            fieldName: 'unit',
            initValue: '',
            dataType: 'text',
            getInputState: () => 'optional',
            showField: () => !isInsideSales,
            getElement(ctx: Ctx) {
              return createTextField(this, ctx);
            },
            validate() {
              return Yup.string().test(
                'valid-address-values',
                validationMessages.noSpecialChars,
                (value) => !/[^\w ,:'()/\-.]|\s{5}/.test(value || ''),
              );
            },
          },
          city: {
            label: tAccount('city', { defaultValue: 'City' }),
            placeholder: tAccount('city', { defaultValue: 'City' }),
            fieldName: 'city',
            initValue: '',
            dataType: 'text',
            getInputState: () => 'required',
            showField: () => !isInsideSales,
            getElement(ctx: Ctx) {
              return createTextField(this, ctx);
            },
            validate() {
              return (
                !isInsideSales &&
                Yup.string().required(tAccount('cityRequired', { defaultValue: 'Please select a city.' }))
              );
            },
          },
          province: {
            label: tAccount('provinceState', { defaultValue: 'Province/State' }),
            placeholder: tAccount('provinceState', { defaultValue: 'Province/State' }),
            fieldName: 'province',
            initValue: '',
            dataType: 'text',
            getInputState: () => 'required',
            showField: () => !isInsideSales,
            getElement(ctx: Ctx) {
              return createTextField(this, ctx);
            },
            validate() {
              return (
                !isInsideSales &&
                Yup.string().required(
                  tAccount('provinceStateRequired', { defaultValue: 'Please enter a province or state.' }),
                )
              );
            },
          },
          country: {
            label: tAccount('country', { defaultValue: 'Country' }),
            placeholder: tAccount('selectACountry', { defaultValue: 'Select a Country' }),
            fieldName: 'country',
            initValue: '',
            dataType: 'mapped',
            getInputState: () => 'required',
            showField: () => !isInsideSales,
            getElement(ctx: Ctx) {
              return createSelectField(this, ctx);
            },
            getSelectOptions: () => countryOptions,
            validate() {
              return Yup.string().required(
                tAccount('selectCountryRequired', { defaultValue: 'Please select a country.' }),
              );
            },
          },
          postalcode: {
            label: tAccount('postalCodeZipCode', { defaultValue: 'Postal Code/Zip Code' }),
            placeholder: tAccount('postalCodeZipCode', { defaultValue: 'Postal Code/Zip Code' }),
            fieldName: 'postalcode',
            initValue: '',
            dataType: 'text',
            getInputState: () => 'required',
            showField: () => !isInsideSales,
            getElement(ctx: Ctx) {
              return createTextField(this, ctx);
            },
            validate() {
              return (
                !isInsideSales &&
                Yup.string().required(tAccount('postalCodeRequired', { defaultValue: 'Please enter a postal code.' }))
              );
            },
          },
          businessPhone: {
            label: tAccount('businessPhoneNumberAddress', { defaultValue: 'Business Phone Number ' }),
            placeholder: '6041111111',
            fieldName: 'businessPhone',
            initValue: '',
            dataType: 'text',
            getInputState: () => 'required',
            showField: () => !isInsideSales,
            getElement(ctx: Ctx) {
              return createTextField(this, ctx);
            },
            validate() {
              return (
                !isInsideSales &&
                Yup.string()
                  .matches(new RegExp(buildMatcher([matchers.numeric]), 'i'), validationMessages.numbers)
                  .test(
                    'len',
                    tAccount('phoneNumberLength', {
                      defaultValue: 'Phone number must be between 6 and 11 characters',
                    }),
                    (val) => (val || '').length >= 6 && (val || '').length <= 11,
                  )
                  .required(
                    tAccount('businessPhoneRequired', { defaultValue: 'Please enter the business phone number.' }),
                  )
              );
            },
          },
        },

        showSection: () => !isInsideSales,
      },
    ],
    accountSettings: [
      {
        label: tAccount('accountSettings', { defaultValue: 'Account Settings' }),
        fields: {
          isClientAccountsEnabled: {
            label: tAccount('enableClientAccounts', { defaultValue: 'Enable Client Accounts' }),
            fieldName: 'isClientAccountsEnabled',
            initValue: false,
            dataType: 'boolean',
            getInputState: () => 'optional',
            showField: () => {
              const nonApplicableTypes = ['ISO'];

              return !isInsideSales && !nonApplicableTypes.includes(partnerType);
            },
            getElement(ctx: Ctx) {
              return createToggleField(this, ctx);
            },
          },
          isIntegrationWithAccountingSoftware: {
            label: tAccount('integrationWithAccountingSystem', { defaultValue: 'Integration with Accounting System' }),
            fieldName: 'isIntegrationWithAccountingSoftware',
            initValue: false,
            dataType: 'boolean',
            getInputState: () => 'optional',
            showField: () => !isInsideSales,
            getElement(ctx: Ctx) {
              return createToggleField(this, ctx);
            },
          },
        },
        showSection: () => !isInsideSales,
      },
    ],
    billingPackage: [
      {
        label: tAccount('billingPackage', { defaultValue: 'Billing Package' }),
        fields: {
          packageType: {
            label: tAccount('billingPackage', { defaultValue: 'Billing Package' }),
            placeholder: tAccount('selectABillingPackage', { defaultValue: 'Select a Billing Package' }),
            fieldName: 'packageType',
            initValue: '',
            dataType: 'mapped',
            showField: () => !permissions.canAccessCustomServiceRequirements && !permissions.partnerBillsAccount,
            getInputState(ctx: Ctx) {
              if (!this.showField(ctx)) return 'disabled';

              return !['PayFac', 'ISO'].includes(partnerType) ? 'required' : 'optional';
            },
            getElement(ctx: Ctx) {
              return createSelectField(this, ctx, undefined, (event) => {
                if (event.target.value !== '') {
                  getPackageDetails(event.target.value);
                }
              });
            },
            getSelectOptions: () => billingPackages.dropDownOptions,
            validate(ctx: Ctx) {
              return this.getInputState(ctx) === 'required'
                ? Yup.string().required(
                    tAccount('billingPackageRequired', { defaultValue: 'Please select a billing package.' }),
                  )
                : Yup.string().notRequired();
            },
          },
        },
        showSection: () => true,
      },
    ],
    ...billingPackageSections,
  };

  return useMemo(
    () => fieldUtilsFactory<NewAccountData>(sections),
    [businessTypes, makeFieldsVisible, billingPackages, packageDetails, tAccount, t, validationMessages],
  );
};

export default useAccountCreationFields;
